import {
  Box,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { SvgIconProps } from "@mui/material/SvgIcon";
import { DropdownIcon, IncremnetDecrementIcon } from "app/icons";
import React from "react";
import { useTranslation } from "react-i18next";
import { FormFieldConfig } from "../types";
import { useStyles } from "./styles";

const stepNumber = (value: string, delta: number) =>
  String(Math.max(0, (Number(value) || 0) + delta));

type FormSectionProps = {
  titleKey: string;
  children: React.ReactNode;
  hideTopBorder?: boolean;
  titleClassName?: string;
};

type FormFieldControlProps<T extends Record<string, string | boolean>> = {
  config: FormFieldConfig<T>;
  value: string;
  onChange: (value: string) => void;
  multiline?: boolean;
  minRows?: number;
  hideLabel?: boolean;
  compact?: boolean;
  dense?: boolean;
  wrapperClassName?: string;
  inputClassName?: string;
  labelClassName?: string;
  selectClassName?: string;
  selectValueClassName?: string;
  selectPlaceholderClassName?: string;
  selectControlClassName?: string;
  selectIconComponent?: React.ElementType<SvgIconProps>;
  replaceInputStyles?: boolean;
};

export const FormSection = ({ titleKey, children, hideTopBorder, titleClassName }: FormSectionProps) => {
  const { t } = useTranslation();
  const styles = useStyles();

  return (
    <Box className={styles.section}>
      <Box className={hideTopBorder ? styles.sectionHeaderPlain : styles.sectionHeader}>
        <Typography className={titleClassName ?? styles.sectionTitle}>{t(titleKey)}</Typography>
      </Box>
      {children}
    </Box>
  );
};

export const FormFieldControl = <T extends Record<string, string | boolean>>({
  config,
  value,
  onChange,
  multiline,
  minRows,
  hideLabel,
  compact,
  dense,
  wrapperClassName,
  inputClassName: additionalInputClassName,
  labelClassName,
  selectClassName,
  selectValueClassName,
  selectPlaceholderClassName,
  selectControlClassName,
  selectIconComponent,
  replaceInputStyles,
}: FormFieldControlProps<T>) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const isTextarea = config.type === "textarea" || multiline;
  const resolveFieldValue = (fieldValue: string) =>
    fieldValue.startsWith("WORK_ORDER.") ? t(fieldValue) : fieldValue;
  const isNarrow = config.narrow;
  const inputClassName = replaceInputStyles && additionalInputClassName
    ? additionalInputClassName
    : [
        isTextarea ? styles.textarea : styles.input,
        config.readOnly ? styles.inputReadOnly : "",
        compact ? styles.inputCompact : "",
        isNarrow ? styles.inputNarrow : "",
        additionalInputClassName,
      ]
        .filter(Boolean)
        .join(" ");

  const fieldClassName = [
    isNarrow
      ? styles.fieldNarrow
      : dense
        ? styles.fieldDense
        : compact
          ? styles.fieldCompact
          : styles.field,
    wrapperClassName,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Box className={fieldClassName}>
      {!hideLabel && (
        <Typography className={labelClassName ?? styles.label}>{t(config.labelKey)}</Typography>
      )}
      {config.type === "select" ? (
        <FormControl fullWidth className={selectControlClassName ?? styles.selectControl}>
          <Select
            className={selectClassName ?? styles.select}
            variant="outlined"
            displayEmpty
            value={value}
            IconComponent={selectIconComponent ?? DropdownIcon}
            onChange={(event) => onChange(event.target.value as string)}
            MenuProps={{
              disableScrollLock: true,
              PaperProps: {
                className: styles.selectMenuPaper,
                ...(config.onMenuScroll ? { onScroll: config.onMenuScroll } : {}),
              },
              MenuListProps: {
                className: styles.selectMenuList,
                ...(config.onMenuScroll ? { onScroll: config.onMenuScroll } : {}),
              },
            }}
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <span className={selectPlaceholderClassName ?? styles.selectPlaceholder}>
                    {config.placeholderKey ? t(config.placeholderKey) : ""}
                  </span>
                );
              }
              const option = config.options?.find(([optionValue]) => optionValue === selected);
              return (
                <span className={selectValueClassName ?? styles.selectValue}>
                  {option ? resolveFieldValue(option[1]) : selected}
                </span>
              );
            }}
          >
            {config.options?.map(([optionValue, labelKey]) => (
              <MenuItem key={optionValue} value={optionValue}>
                {resolveFieldValue(labelKey)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : config.type === "number" && config.showSpinners ? (
        <Box className={styles.stepperBox}>
          <Box className={styles.stepperArrows}>
            <IncremnetDecrementIcon className={styles.stepperIcon} />
            <Box
              className={styles.stepperHitUp}
              onClick={() => onChange(stepNumber(value, 1))}
            />
            <Box
              className={styles.stepperHitDown}
              onClick={() => onChange(stepNumber(value, -1))}
            />
          </Box>
          <input
            className={styles.stepperValue}
            value={resolveFieldValue(value)}
            readOnly={config.readOnly}
            onChange={(event) => onChange(event.target.value)}
          />
        </Box>
      ) : (
        <TextField
          className={inputClassName}
          fullWidth={!isNarrow}
          variant="outlined"
          type={isTextarea ? undefined : config.type ?? "text"}
          multiline={isTextarea}
          minRows={isTextarea ? minRows ?? 4 : undefined}
          placeholder={config.placeholderKey ? t(config.placeholderKey) : undefined}
          value={resolveFieldValue(value)}
          onChange={(event) => onChange(event.target.value)}
          InputProps={{
            readOnly: config.readOnly,
            inputProps: {
              style: {
                textAlign: "right",
                direction: config.type === "number" ? "ltr" : "rtl",
              },
            },
          }}
        />
      )}
    </Box>
  );
};

export const renderFieldControls = <T extends Record<string, string | boolean>>(
  fields: FormFieldConfig<T>[],
  formValues: T,
  updateField: (field: keyof T, value: string | boolean) => void
) =>
  fields.map((config) => (
    <FormFieldControl
      key={String(config.field)}
      config={config}
      value={formValues[config.field] as string}
      onChange={(value) => updateField(config.field, value)}
    />
  ));

export const getTextDirection = (language: string) =>
  (language || "hw").startsWith("hw") ? "rtl" : "ltr";
